import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useMemo, useState, useRef, memo, FC, useCallback } from 'react';
import { useRequest } from 'ahooks';
import useResizeObserver from 'use-resize-observer';
import { Empty, Icon, Spin, Tabs, Button, Menu, Input, Avatar } from 'udesk-ui';
import { TreeMenu } from 'udesk_gm_ui';
import Ellipsis from 'src/components/common/Ellipsis';
import { PeopleGroup1Outlined } from '@udesk/icons';
import { getFeishuDepsInit } from 'src/api/feishuDeps/init';
import { getFeishuDepsRoot } from 'src/api/feishuDeps/root';
import { getFeishuDepsByDepId } from 'src/api/feishuDeps/{depId}';
import { getFeishuDepsSearch } from 'src/api/feishuDeps/search';
import { getFeishuSessions } from 'src/api/feishuSessions';
import { FeishuSessionFoundResponse } from 'src/api/types';
import { DataNode, getNode, updateExpandNode, updateTreeData } from './utils';

let timer: any = null;

interface TemplateProps {
    onChartChange: (key: string) => void;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { onChartChange } = props;

    const locales = UdeskLocales['current'];

    const { ref: containerDomRef, height = 1 } = useResizeObserver<HTMLDivElement>();

    const { loading: getFeishuDepsInitLoading } = useRequest(getFeishuDepsInit, {
        onSuccess: () => {
            getDepartments();
        },
    });

    const [treeData, setTreeData] = useState<DataNode[]>([]);
    const [defaultExpandedKeys, setDefaultExpandedKeys] = useState<string[]>([]);

    const { run: getDepartments, loading: getFeishuDepsRootLoading } = useRequest(
        () => getFeishuDepsRoot(),
        {
            manual: true,
            onSuccess: (res) => {
                setTreeData(
                    updateExpandNode(
                        (res.data || []).map((d) => getNode(d)),
                        [`${res.data?.[0].depId}`]
                    )
                );
                setDefaultExpandedKeys([`${res.data?.[0].depId}`]);
            },
        }
    );

    const expandedKeys = useRef<string[]>([]);
    // 选中的组织或者员工的key
    const [selectedKey, setSelectedKey] = useState<string>('');
    const [depId, setDepId] = useState<string>();
    const [employeeId, setEmployeeId] = useState<string>();
    const [lastId, setLastId] = useState<number>();
    const [chartList, setChartList] = useState<FeishuSessionFoundResponse[]>();

    const onSelect = useCallback((selectedKeys, { selected, selectedNodes, node, event }) => {
        if (selected) {
            setSelectedKey(node.key);
            setChartList([]);
            setSelectedKeys([]);

            if (node.isEmployee) {
                setDepId(node.parentId);
                setEmployeeId(node.id);
                setLastId(undefined);
            } else {
                setDepId(node.key);
                setEmployeeId(undefined);
                setLastId(undefined);
            }
        }
    }, []);

    const treeProps = useMemo(() => {
        const onExpand = (newExpandedKeys) => {
            setTreeData(updateExpandNode(treeData, newExpandedKeys));
            expandedKeys.current = newExpandedKeys;
        };

        const loadData = ({ key, children }: any) =>
            new Promise<void>((resolve) => {
                if (children) {
                    resolve();
                    return;
                }

                clearTimeout(timer);
                timer = setTimeout(() => {
                    getFeishuDepsByDepId({ segments: { depId: key } }).then?.(
                        (resp) => {
                            setTreeData((origin) =>
                                updateExpandNode(
                                    updateTreeData(origin, key, getNode(resp.data || {})),
                                    expandedKeys.current
                                )
                            );
                            resolve();
                            timer = null;
                        },
                        (reason) => {
                            Udesk.ui.notify.error(reason.errorMsg);
                            timer = null;
                        }
                    );
                }, 100);
            });

        return {
            treeData,
            loadData,
            blockNode: true,
            onSelect,
            selectedKeys: [selectedKey],
            onExpand,
            showIcon: true,
            height,
        };
    }, [treeData, onSelect, selectedKey, height]);

    const [filterTreeData, setFilterTreeData] = useState<DataNode[]>([]);

    const filterTreeProps = useMemo(() => {
        return {
            onSelect,
            selectedKeys: [selectedKey],
            showIcon: true,
            treeData: filterTreeData,
            virtual: false,
            height,
        };
    }, [onSelect, selectedKey, filterTreeData, height]);

    const [isFilter, setIsFilter] = useState(false);

    const [filterTreeDataLoading, setFilterTreeDataLoading] = useState(false);

    const filterKeywords = (value: string) => {
        setIsFilter(Boolean(value));
        setFilterTreeData([]);
        setFilterTreeDataLoading(true);
        getFeishuDepsSearch({ params: { name: value } })
            .then((resp) => {
                setFilterTreeData(
                    updateExpandNode(
                        (resp.data || []).map((d) => getNode(d)),
                        []
                    )
                );
            })
            .finally(() => {
                setFilterTreeDataLoading(false);
            });
    };

    const [hasMore, setHasMore] = useState<boolean>(false);
    // 选中的群聊key
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    useRequest(() => getFeishuSessions({ params: { depId, employeeId, lastId } }), {
        refreshDeps: [depId, employeeId, lastId],
        onSuccess: (res) => {
            const list = res.data || [];
            const newList = [...(chartList || []), ...list];
            setChartList(newList);
            setHasMore(list.length === 10);

            // 如果没有选中项，且有数据，则默认选中第一项，并触发回调
            if (!selectedKeys.length && newList.length) {
                const key = newList[0].id! + '';
                setSelectedKeys([key]);
                onChartChange?.(key);
            }
        },
    });

    const loadMore = () => {
        const lId = chartList?.[chartList.length - 1]?.id;

        if (lId) {
            setLastId(lId);
        }
    };

    const handleClick = ({ key }) => {
        setSelectedKeys([key]);
        onChartChange?.(key);
    };

    const [isOpen, setIsOpen] = useState(true);

    const changeSiderBarStatus = () => {
        setIsOpen(!isOpen);
    };

    const [chartType] = useState(Udesk.enums.chatTypes.groupChat.id);

    const [chartNameKeyword, setChartNameKeyword] = useState<string>();

    const showChartList = useMemo(() => {
        if (chartNameKeyword?.length) {
            return chartList?.filter?.((d) => d.chatName?.includes(chartNameKeyword));
        } else {
            return chartList;
        }
    }, [chartList, chartNameKeyword]);

    return (
        <React.Fragment>
            <Spin tip="Loading..." spinning={getFeishuDepsInitLoading || getFeishuDepsRootLoading}>
                <div
                    className={`wechat-qa-chart-record-detail-left-filter-list ${
                        isOpen ? '' : 'closed'
                    }`}
                >
                    <Input.Search
                        onChange={(e) => !e.target.value && setIsFilter(false)}
                        allowClear
                        onSearch={filterKeywords}
                        style={{ marginBottom: 8 }}
                    />

                    <div
                        className="wechat-qa-chart-record-detail-left-filter-list-content"
                        ref={containerDomRef}
                    >
                        {!isFilter ? (
                            defaultExpandedKeys.length > 0 ? (
                                <TreeMenu
                                    {...treeProps}
                                    defaultExpandedKeys={defaultExpandedKeys}
                                    key="isNotFilter"
                                />
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            )
                        ) : filterTreeDataLoading && !filterTreeData.length ? (
                            <Spin tip="Loading..." spinning={!filterTreeData.length}>
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    style={{ width: '100%' }}
                                />
                            </Spin>
                        ) : (
                            <TreeMenu {...filterTreeProps} defaultExpandAll key="isFilter" />
                        )}
                    </div>
                </div>

                {treeData.length > 0 && (
                    <div
                        className="wechat-qa-chart-record-detail-left-filter-list"
                        style={isOpen ? { borderLeft: '1px solid rgb(240, 240, 240)' } : undefined}
                    >
                        <Tabs
                            size="small"
                            tabBarExtraContent={{
                                left: (
                                    <Button
                                        type="text"
                                        onClick={changeSiderBarStatus}
                                        style={{ marginRight: 8 }}
                                        icon={
                                            <Icon
                                                type={
                                                    !isOpen
                                                        ? 'MenuUnfoldOutlined'
                                                        : 'MenuFoldOutlined'
                                                }
                                                antdIcon={true}
                                            ></Icon>
                                        }
                                    ></Button>
                                ),
                            }}
                            activeKey={chartType}
                        >
                            <Tabs.TabPane
                                tab={locales.fix.groupChat}
                                key={Udesk.enums.chatTypes.groupChat.id}
                            ></Tabs.TabPane>
                        </Tabs>

                        {selectedKey && (
                            <Input.Search
                                placeholder={
                                    /* 请输入群聊名称 */ locales.pages.wechatQa.chartRecord.detail
                                        .components.filter.index.pleaseEnterTheGroupChatName
                                }
                                allowClear
                                style={{ marginBottom: 8, paddingLeft: 16 }}
                                onSearch={(value) => setChartNameKeyword(value)}
                                onChange={(e) => {
                                    if (!e.target.value) {
                                        setChartNameKeyword(undefined);
                                    }
                                }}
                            />
                        )}

                        {showChartList?.length ? (
                            <div className="wechat-qa-chart-record-detail-left-filter-list-content">
                                <Menu
                                    selectedKeys={selectedKeys}
                                    onSelect={handleClick}
                                    mode="inline"
                                >
                                    {showChartList.map((item, index) => {
                                        return (
                                            <Menu.Item key={item.id}>
                                                <div
                                                    className="wechat-qa-chart-record-detail-left-filter-list-item"
                                                    style={{ justifyContent: 'flex-start' }}
                                                >
                                                    <div
                                                        className="wechat-qa-chart-record-detail-left-filter-list-item-left"
                                                        style={{ flex: '32px 0 0' }}
                                                    >
                                                        <Avatar
                                                            src={item.chatAvatar}
                                                            icon={<PeopleGroup1Outlined />}
                                                        />
                                                    </div>
                                                    <div
                                                        className="wechat-qa-chart-record-detail-left-filter-list-item-right"
                                                        style={{ width: 'inherit' }}
                                                    >
                                                        <div
                                                            style={{
                                                                fontWeight: 'bold',
                                                                color: '#000',
                                                            }}
                                                        >
                                                            <Ellipsis text={item.chatName} />
                                                        </div>
                                                        <div style={{ color: '#000' }}>
                                                            <Ellipsis
                                                                text={`${
                                                                    item.lastMsgUserName ?? ''
                                                                }: ${item.lastMsg ?? ''}`}
                                                            />
                                                        </div>
                                                        <div>{item.lastMsgTime}</div>
                                                    </div>
                                                </div>
                                            </Menu.Item>
                                        );
                                    })}
                                </Menu>

                                {hasMore ? (
                                    <div style={{ textAlign: 'center' }}>
                                        <Button type="link" size="small" onClick={loadMore}>
                                            {/* 加载更多 */}
                                            {
                                                UdeskLocales['current'].pages.feishuQa.chatRecord
                                                    .components.filter.index.loadMore
                                            }
                                        </Button>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </div>
                )}
            </Spin>
        </React.Fragment>
    );
});

export default Template;
